/**
 * Contract Form Component
 * Task 44: Form for creating/editing contracts with recurrence and payment terms
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { IndustryContract } from '@/types/industry';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const contractSchema = z.object({
  client_id: z.coerce.number().optional(),
  industry_project_id: z.string().optional(),
  start_date: z.date(),
  end_date: z.date().optional(),
  value: z.coerce.number().min(0, 'Valor deve ser maior ou igual a zero'),
  recurrence: z.enum(['monthly', 'quarterly', 'semiannual', 'annual', 'custom', 'none']).default('none'),
  payment_day: z.coerce.number().min(1).max(31).optional(),
  description: z.string().optional(),
  payment_terms: z.string().optional(),
});

type ContractFormData = z.infer<typeof contractSchema>;

interface ContractFormProps {
  contract?: IndustryContract;
  clients?: Array<{ id: number; name: string }>;
  projects?: Array<{ id: string; name: string }>;
  onSubmit: (data: ContractFormData) => void | Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
}

const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'Sem Recorrência (Pagamento Único)' },
  { value: 'monthly', label: 'Mensal' },
  { value: 'quarterly', label: 'Trimestral' },
  { value: 'semiannual', label: 'Semestral' },
  { value: 'annual', label: 'Anual' },
  { value: 'custom', label: 'Personalizado' },
];

export function ContractForm({
  contract,
  clients = [],
  projects = [],
  onSubmit,
  onCancel,
  isSubmitting = false,
}: ContractFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<ContractFormData>({
    resolver: zodResolver(contractSchema),
    defaultValues: contract
      ? {
          client_id: contract.client_id || undefined,
          industry_project_id: contract.industry_project_id || undefined,
          start_date: contract.start_date ? new Date(contract.start_date) : new Date(),
          end_date: contract.end_date ? new Date(contract.end_date) : undefined,
          value: contract.value,
          recurrence: (contract.metadata?.recurrence as any) || 'none',
          payment_day: (contract.metadata?.paymentDay as number) || undefined,
          description: (contract.metadata?.description as string) || '',
          payment_terms: (contract.metadata?.paymentTerms as string) || '',
        }
      : {
          start_date: new Date(),
          value: 0,
          recurrence: 'none',
        },
  });

  const startDate = watch('start_date');
  const endDate = watch('end_date');
  const recurrence = watch('recurrence');
  const selectedClient = watch('client_id');
  const selectedProject = watch('industry_project_id');

  const handleFormSubmit = async (data: ContractFormData) => {
    await onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="space-y-6">
        {/* Client/Project Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Cliente ou Projeto</CardTitle>
            <CardDescription>Selecione o cliente ou projeto relacionado</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Client */}
            <div className="space-y-2">
              <Label htmlFor="client_id">Cliente</Label>
              <Select
                value={selectedClient?.toString() || 'none'}
                onValueChange={(value) =>
                  setValue('client_id', value === 'none' ? undefined : parseInt(value, 10))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um cliente (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id.toString()}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.client_id && (
                <p className="text-sm text-destructive">{errors.client_id.message}</p>
              )}
            </div>

            {/* Project */}
            <div className="space-y-2">
              <Label htmlFor="industry_project_id">Projeto</Label>
              <Select
                value={selectedProject || 'none'}
                onValueChange={(value) => setValue('industry_project_id', value === 'none' ? undefined : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um projeto (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.industry_project_id && (
                <p className="text-sm text-destructive">{errors.industry_project_id.message}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Descrição do Contrato</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder="Descrição detalhada do contrato"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Contract Terms */}
        <Card>
          <CardHeader>
            <CardTitle>Vigência e Valor</CardTitle>
            <CardDescription>Defina o período e valor do contrato</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Start Date */}
              <div className="space-y-2">
                <Label>
                  Data de Início <span className="text-destructive">*</span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !startDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'dd/MM/yyyy') : 'Selecione a data'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => date && setValue('start_date', date)}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
                {errors.start_date && (
                  <p className="text-sm text-destructive">{errors.start_date.message}</p>
                )}
              </div>

              {/* End Date */}
              <div className="space-y-2">
                <Label>Data de Término</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !endDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, 'dd/MM/yyyy') : 'Sem data de término'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={(date) => setValue('end_date', date)}
                      locale={ptBR}
                      disabled={(date) => startDate && date < startDate}
                    />
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                  Deixe vazio para contratos sem data de término definida
                </p>
              </div>
            </div>

            {/* Value */}
            <div className="space-y-2">
              <Label htmlFor="value">
                Valor do Contrato (R$) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="value"
                type="number"
                step="0.01"
                min="0"
                {...register('value')}
                placeholder="0,00"
              />
              {errors.value && <p className="text-sm text-destructive">{errors.value.message}</p>}
            </div>
          </CardContent>
        </Card>

        {/* Recurrence */}
        <Card>
          <CardHeader>
            <CardTitle>Recorrência de Pagamento</CardTitle>
            <CardDescription>Configure cobranças automáticas periódicas</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Recurrence Type */}
            <div className="space-y-2">
              <Label htmlFor="recurrence">Tipo de Recorrência</Label>
              <Select
                value={recurrence}
                onValueChange={(value: any) => setValue('recurrence', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RECURRENCE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Payment Day (only if recurring) */}
            {recurrence !== 'none' && (
              <div className="space-y-2">
                <Label htmlFor="payment_day">Dia de Pagamento</Label>
                <Input
                  id="payment_day"
                  type="number"
                  min="1"
                  max="31"
                  {...register('payment_day')}
                  placeholder="Ex: 5 (dia 5 de cada período)"
                />
                {errors.payment_day && (
                  <p className="text-sm text-destructive">{errors.payment_day.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Dia do mês em que o pagamento deve ser gerado (1-31)
                </p>
              </div>
            )}

            {/* Payment Terms */}
            <div className="space-y-2">
              <Label htmlFor="payment_terms">Condições de Pagamento</Label>
              <Textarea
                id="payment_terms"
                {...register('payment_terms')}
                placeholder="Ex: Boleto bancário com vencimento no dia 5, desconto de 2% para pagamento antecipado"
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Detalhes sobre formas de pagamento, prazos, descontos, etc.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-2 justify-end">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancelar
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Salvando...' : contract ? 'Atualizar Contrato' : 'Criar Contrato'}
          </Button>
        </div>
      </div>
    </form>
  );
}
